import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { X, Download } from 'lucide-react';
import { Actions as USAGEACTIONS } from '../../store/actions/usageActions';

const ERROR_LIST_LIMIT = 10;

const buildErrorCsv = (errors) =>
  ['Row,Error', ...errors.map(({ row, err }) => `${row ?? ''},"${String(err ?? '').replace(/"/g, '""')}"`)].join('\n');

const ImportResultModal = () => {
  const dispatch = useDispatch();
  const importModal = useSelector((state) => state.importModal);
  const importSuccesses = importModal?.importSuccesses;
  const importErrors = importModal?.importErrors || [];
  const errorLink = importModal?.errorLink;
  const templateError = importModal?.templateError;
  const lastRowNumber = importModal?.lastRowNumber;

  const closeModal = useCallback(() => {
    dispatch(USAGEACTIONS.showAnalysisImportModal(false, null));
    dispatch(USAGEACTIONS.showUploadUsageDataModal(false));
  }, [dispatch]);

  const downloadErrorCsv = useCallback(() => {
    const blob = new Blob([buildErrorCsv(importErrors)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'import-errors.csv';
    link.click();
    URL.revokeObjectURL(url);
  }, [importErrors]);

  if (!importModal) return null;

  const listedErrors = importErrors.slice(0, ERROR_LIST_LIMIT);
  const isTruncated = importErrors.length > ERROR_LIST_LIMIT;
  const showCsvFallback = isTruncated && !errorLink;

  return (
    <Dialog open={!!importModal} onOpenChange={closeModal}>
      <DialogContent className='max-w-lg p-0 bg-background'>
        <Card className='bg-card text-card-foreground border-none shadow-none'>
          <DialogHeader className='bg-header text-header-foreground px-6 pt-6 pb-2'>
            <DialogTitle className='font-default flex items-center justify-between'>
              Import Analysis
              <Button
                variant='ghost'
                size='icon'
                className='ml-auto text-muted-foreground hover:text-foreground'
                onClick={closeModal}>
                <X size={16} strokeWidth={1.5} />
              </Button>
            </DialogTitle>
          </DialogHeader>
          <CardContent className='p-6'>
            <div className='mb-4'>
              {lastRowNumber === 2 && importSuccesses < 1 && importErrors.length < 1 && !templateError && (
                <div className='text-destructive font-bold text-base mb-2'>
                  No rows imported due to blank first row in import template.
                </div>
              )}
              {importSuccesses > 0 && (
                <div className='text-success font-bold text-base mb-2'>
                  {importSuccesses} {importSuccesses > 1 ? 'rows' : 'row'} imported successfully
                </div>
              )}
              {templateError && <div className='text-destructive font-bold text-base mb-2'>{templateError}</div>}
            </div>
            {importErrors.length > 0 && (
              <div className='mb-4'>
                <div className='text-destructive font-bold text-base mb-2'>
                  {importErrors.length > 1
                    ? `Encountered ${importErrors.length} errors on the following rows:`
                    : 'Encountered 1 error on the following row:'}
                </div>
                <div className='max-h-64 overflow-y-auto'>
                  {listedErrors.map((item, key) => (
                    <div className='ml-2 mb-1 text-sm' key={key}>
                      <span className='font-bold'>Row {item.row}:</span>
                      <span> {item.err}</span>
                    </div>
                  ))}
                </div>
                {isTruncated && (
                  <div className='ml-2 mt-2 text-sm text-muted-foreground'>
                    Showing the first {ERROR_LIST_LIMIT} errors. Download the error file to view all of them.
                  </div>
                )}
              </div>
            )}
            {errorLink && (
              <div className='mb-4'>
                <div className='text-destructive font-bold text-base mb-2'>
                  Received more than {ERROR_LIST_LIMIT} errors: Please download the{' '}
                  <a className='underline text-primary' href={errorLink} target='_blank' rel='noopener noreferrer'>
                    error file
                  </a>{' '}
                  to view all errors.
                </div>
              </div>
            )}
            <div className='flex flex-row-reverse gap-2 mt-6'>
              {errorLink && (
                <a href={errorLink} target='_blank' rel='noopener noreferrer'>
                  <Button variant='default' className='bg-primary text-primary-foreground'>
                    <Download size={16} strokeWidth={1.5} className='mr-2' /> Download Error File
                  </Button>
                </a>
              )}
              {showCsvFallback && (
                <Button variant='default' className='bg-primary text-primary-foreground' onClick={downloadErrorCsv}>
                  <Download size={16} strokeWidth={1.5} className='mr-2' /> Download Error File
                </Button>
              )}
              <Button variant='secondary' onClick={closeModal}>
                Close
              </Button>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  );
};

export default ImportResultModal;
